import { useState } from 'react'
import { Card, CardContent } from '../ui/card'
import { TabProps } from './types'
import { ChartWrapper } from './ChartWrapper'
import { DateAdherenceKPIChart } from './charts/DateAdherenceKPIChart'
import { Calendar, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react'
import { OTDDrilldownModal } from '../drilldown'

export function DateAdherenceTab({ data, currency: _currency, filters: _filters }: TabProps) {
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState('')
  const [otdModalInitialTab, setOtdModalInitialTab] = useState<'on_time' | 'early' | 'late' | 'total'>('total')

  const adherenceData = data.charts.dateAdherenceData?.map(d => ({
    supplier: '',
    customer: d.customer,
    month: d.month,
    total: d.total,
    onTime: d.onTime,
    adherence: d.adherence,
  })) || []

  // Calculate summary metrics from real data
  const totalRecords = adherenceData.length
  const totalLines = adherenceData.reduce((sum, d) => sum + d.total, 0)
  const totalOnTime = adherenceData.reduce((sum, d) => sum + d.onTime, 0)
  const overallAdherence = totalLines > 0 ? Math.round((totalOnTime / totalLines) * 100) : 0
  const uniqueCustomers = new Set(adherenceData.map(d => d.customer)).size

  // Count cells by adherence band
  const excellent = adherenceData.filter(d => d.adherence >= 95).length
  const good = adherenceData.filter(d => d.adherence >= 85 && d.adherence < 95).length
  const atRisk = adherenceData.filter(d => d.adherence >= 60 && d.adherence < 85).length
  const poor = adherenceData.filter(d => d.adherence < 60).length

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">Date Adherence KPI</h2>
        <p className="text-sm text-muted-foreground">
          Supplier delivery date adherence breakdown by supplier and month
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Adherence</p>
                <p className="text-2xl font-bold">{overallAdherence}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">On Time</p>
                <p className="text-2xl font-bold text-green-600">{totalOnTime} / {totalLines}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Customers Tracked</p>
                <p className="text-2xl font-bold">{uniqueCustomers}</p>
              </div>
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">At Risk Cells</p>
                <p className="text-2xl font-bold text-amber-600">{atRisk + poor}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Heatmap */}
      <ChartWrapper
        title="Supplier Date Adherence Heatmap"
        exportFilename="date-adherence-kpi"
      >
        <DateAdherenceKPIChart
          data={adherenceData.length > 0 ? adherenceData : undefined}
          onCellClick={(cellData) => {
            setOtdModalContext(`${cellData.customer} - ${cellData.month}`)
            setOtdModalInitialTab('total')
            setIsOTDModalOpen(true)
          }}
        />
      </ChartWrapper>

      {/* Adherence Band Distribution */}
      {totalRecords > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="shadow-sm bg-green-50 border-green-200">
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-green-700 font-medium">Excellent (≥95%)</p>
              <p className="text-2xl font-bold text-green-700">{excellent}</p>
              <p className="text-xs text-green-600">{totalRecords > 0 ? Math.round((excellent / totalRecords) * 100) : 0}% of cells</p>
            </CardContent>
          </Card>
          <Card className="shadow-sm bg-lime-50 border-lime-200">
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-lime-700 font-medium">Good (85-94%)</p>
              <p className="text-2xl font-bold text-lime-700">{good}</p>
              <p className="text-xs text-lime-600">{totalRecords > 0 ? Math.round((good / totalRecords) * 100) : 0}% of cells</p>
            </CardContent>
          </Card>
          <Card className="shadow-sm bg-amber-50 border-amber-200">
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-amber-700 font-medium">At Risk (60-84%)</p>
              <p className="text-2xl font-bold text-amber-700">{atRisk}</p>
              <p className="text-xs text-amber-600">{totalRecords > 0 ? Math.round((atRisk / totalRecords) * 100) : 0}% of cells</p>
            </CardContent>
          </Card>
          <Card className="shadow-sm bg-red-50 border-red-200">
            <CardContent className="pt-4 text-center">
              <p className="text-sm text-red-700 font-medium">Poor (&lt;60%)</p>
              <p className="text-2xl font-bold text-red-700">{poor}</p>
              <p className="text-xs text-red-600">{totalRecords > 0 ? Math.round((poor / totalRecords) * 100) : 0}% of cells</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Date adherence is calculated from goods receipt notes (GRNs),
          comparing actual receipt dates against scheduled delivery dates. Data is grouped by supplier
          and month. Click any cell to drill down into delivery details.
        </p>
      </div>

      {/* OTD Drilldown Modal */}
      <OTDDrilldownModal
        isOpen={isOTDModalOpen}
        onClose={() => setIsOTDModalOpen(false)}
        contextLabel={otdModalContext}
        initialTab={otdModalInitialTab}
      />
    </div>
  )
}
